import { Anchor, Globe, Sailboat, Snowflake, Trees } from "lucide-react";
import { REGION_ORDER, REGION_LABELS, type RegionKey } from "@/lib/region";

export type RegionValue = "all" | RegionKey;

export function isRegionKey(v: unknown): v is RegionKey {
  return typeof v === "string" && (REGION_ORDER as string[]).includes(v);
}

export function parseRegionParam(v: unknown): RegionValue {
  return isRegionKey(v) ? v : "all";
}

const REGION_ICONS: Record<RegionValue, typeof Globe> = {
  all: Globe,
  lapland: Snowflake,
  lakeland: Trees,
  archipelago: Sailboat,
  coast: Anchor,
};

const REGION_SHORT: Record<RegionValue, string> = {
  all: "All regions",
  lapland: "Lapland",
  lakeland: "Lakeland",
  archipelago: "Archipelago",
  coast: "Coast",
};

export function RegionFilter({
  value,
  onChange,
  counts,
  className,
}: {
  value: RegionValue;
  onChange: (next: RegionValue) => void;
  counts?: Partial<Record<RegionValue, number>>;
  className?: string;
}) {
  const items: { key: RegionValue; label: string }[] = [
    { key: "all", label: REGION_SHORT.all },
    ...REGION_ORDER.map((k) => ({ key: k as RegionValue, label: REGION_SHORT[k] })),
  ];
  return (
    <div
      className={`flex items-center gap-2 overflow-x-auto no-scrollbar snap-x snap-mandatory -mx-1 px-1 py-1 -my-1 sm:mx-0 sm:px-0 sm:overflow-visible ${className ?? ""}`}
      role="group"
      aria-label="Filter by travel region"
    >
      {items.map((it) => {
        const active = value === it.key;
        const count = counts?.[it.key];
        const disabled = count === 0 && it.key !== "all" && value !== it.key;
        const Icon = REGION_ICONS[it.key];
        const fullLabel = it.key === "all" ? "All regions" : REGION_LABELS[it.key as RegionKey];
        return (
          <button
            key={it.key}
            type="button"
            title={fullLabel}
            onClick={() => !disabled && onChange(it.key)}
            disabled={disabled}
            aria-pressed={active}
            className={`shrink-0 snap-start sm:flex-1 sm:min-w-0 h-10 inline-flex items-center justify-center gap-2 rounded-full pl-1.5 pr-3 sm:px-2 text-[12.5px] font-semibold leading-none font-ui not-italic normal-case transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-navy/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
              active
                ? "bg-navy text-white ring-1 ring-navy shadow-[0_8px_18px_-10px_rgb(0_0_0/0.6)]"
                : disabled
                  ? "bg-navy/[0.03] text-navy/30 ring-1 ring-navy/[0.06] cursor-not-allowed"
                  : "bg-white text-navy/80 ring-1 ring-navy/12 hover:text-navy hover:ring-navy/30 hover:shadow-[0_4px_12px_-8px_rgb(0_0_0/0.4)] active:scale-[0.98]"
            }`}
          >
            <span
              className={`size-7 rounded-full grid place-items-center shrink-0 transition-colors ${
                active ? "bg-white/15 text-white" : disabled ? "bg-navy/[0.04] text-navy/30" : "bg-navy/[0.05] text-navy/70"
              }`}
            >
              <Icon className="size-3.5" strokeWidth={2} />
            </span>
            <span className="whitespace-nowrap sm:truncate">{it.label}</span>
            {typeof count === "number" && (it.key === "all" || count > 0) && (
              <span
                className={`grid min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold tabular-nums place-items-center shrink-0 ${
                  active ? "bg-white/20 text-white" : "bg-navy/[0.06] text-navy/55"
                }`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
